import { useTranslation } from 'react-i18next';
import { Search, RotateCcw } from 'lucide-react';
import { clsx } from 'clsx';
import type { BankAccount, SharedBankAccount } from '../../types';
import AccountMultiSelect from './AccountMultiSelect';
import CategoryMultiSelect, { type CategoryNode, type CategorySelection } from './CategoryMultiSelect';
import type {
  RecurrenceMode,
  ValidationMode,
  RecurrenceFrequency,
} from '../../services/search';

export interface SearchFiltersState {
  dateStart: string;
  dateEnd: string;
  selectedPersonalIds: string[];
  selectedSharedIds: string[];
  categories: CategorySelection;
  operationType: 'all' | 'credit' | 'debit';
  amountMin: string;
  amountMax: string;
  recurring: RecurrenceMode;
  recurrenceFrequencies: RecurrenceFrequency[];
  validated: ValidationMode;
  payerPartnerId: 'all' | 'partner1' | 'partner2';
  searchText: string;
}

export function countActiveFilters(state: SearchFiltersState): number {
  let n = 0;
  if (state.dateStart) n++;
  if (state.dateEnd) n++;
  if (state.selectedPersonalIds.length) n++;
  if (state.selectedSharedIds.length) n++;
  if (state.categories.expenseCategoryIds.length) n++;
  if (state.categories.expenseSubcategoryIds.length) n++;
  if (state.categories.incomeCategoryIds.length) n++;
  if (state.categories.incomeSubcategoryIds.length) n++;
  if (state.operationType !== 'all') n++;
  if (state.amountMin) n++;
  if (state.amountMax) n++;
  if (state.recurring !== 'all') n++;
  if (state.recurrenceFrequencies.length) n++;
  if (state.validated !== 'all') n++;
  if (state.payerPartnerId !== 'all') n++;
  if (state.searchText) n++;
  return n;
}

export const EMPTY_FILTERS: SearchFiltersState = {
  dateStart: '',
  dateEnd: '',
  selectedPersonalIds: [],
  selectedSharedIds: [],
  categories: {
    expenseCategoryIds: [],
    expenseSubcategoryIds: [],
    incomeCategoryIds: [],
    incomeSubcategoryIds: [],
  },
  operationType: 'all',
  amountMin: '',
  amountMax: '',
  recurring: 'all',
  recurrenceFrequencies: [],
  validated: 'all',
  payerPartnerId: 'all',
  searchText: '',
};

export type SearchFieldError = 'dateStart' | 'dateEnd' | 'amountMin' | 'amountMax';
export type SearchFieldErrors = Partial<Record<SearchFieldError, string>>;

interface SearchFiltersProps {
  filters: SearchFiltersState;
  onFiltersChange: (filters: SearchFiltersState) => void;
  onSubmit: () => void;
  onReset: () => void;
  personalAccounts: BankAccount[];
  sharedAccounts: SharedBankAccount[];
  expenseCategories: CategoryNode[];
  incomeCategories: CategoryNode[];
  partner1Name: string;
  partner2Name: string;
  isLoading: boolean;
  errors?: SearchFieldErrors;
  hideActions?: boolean;
}

const RECURRENCE_FREQUENCY_OPTIONS: RecurrenceFrequency[] = [
  'MONTHLY_DAY',
  'MONTHLY_WEEKDAY',
  'WEEKLY',
  'EVERY_N_WEEKS',
  'INSTALLMENTS',
];

export default function SearchFilters({
  filters,
  onFiltersChange,
  onSubmit,
  onReset,
  personalAccounts,
  sharedAccounts,
  expenseCategories,
  incomeCategories,
  partner1Name,
  partner2Name,
  isLoading,
  errors,
  hideActions = false,
}: SearchFiltersProps) {
  const { t } = useTranslation('search');
  const { t: tc } = useTranslation('common');

  const partnerFilterDisabled = filters.selectedPersonalIds.length > 0;

  const update = (patch: Partial<SearchFiltersState>) => {
    onFiltersChange({ ...filters, ...patch });
  };

  const toggleRecurrenceFrequency = (freq: RecurrenceFrequency) => {
    const current = filters.recurrenceFrequencies;
    if (current.includes(freq)) {
      update({ recurrenceFrequencies: current.filter((f) => f !== freq) });
    } else {
      update({ recurrenceFrequencies: [...current, freq] });
    }
  };


  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
      className="bg-white rounded-xl border border-gray-200 p-4 space-y-4"
    >
      {/* Recherche texte */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          {t('filters.searchText')}
        </label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={filters.searchText}
            onChange={(e) => update({ searchText: e.target.value })}
            placeholder={t('filters.searchPlaceholder')}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Période + comptes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('filters.dateRange')}</label>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="date"
              value={filters.dateStart}
              onChange={(e) => update({ dateStart: e.target.value })}
              aria-invalid={!!errors?.dateStart}
              className={clsx(
                'px-2 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2',
                errors?.dateStart
                  ? 'border-rose-400 focus:ring-rose-500 bg-rose-50'
                  : 'border-gray-300 focus:ring-primary-500',
              )}
            />
            <input
              type="date"
              value={filters.dateEnd}
              onChange={(e) => update({ dateEnd: e.target.value })}
              aria-invalid={!!errors?.dateEnd}
              className={clsx(
                'px-2 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2',
                errors?.dateEnd
                  ? 'border-rose-400 focus:ring-rose-500 bg-rose-50'
                  : 'border-gray-300 focus:ring-primary-500',
              )}
            />
          </div>
          {(errors?.dateStart || errors?.dateEnd) && (
            <p className="mt-1 text-xs text-rose-600">{errors.dateStart || errors.dateEnd}</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('filters.accounts')}</label>
          <AccountMultiSelect
            personalAccounts={personalAccounts}
            sharedAccounts={sharedAccounts}
            selectedPersonalIds={filters.selectedPersonalIds}
            selectedSharedIds={filters.selectedSharedIds}
            onChange={(personalIds, sharedIds) => {
              const patch: Partial<SearchFiltersState> = {
                selectedPersonalIds: personalIds,
                selectedSharedIds: sharedIds,
              };
              if (personalIds.length > 0 && filters.payerPartnerId !== 'all') {
                patch.payerPartnerId = 'all';
              }
              update(patch);
            }}
          />
        </div>
      </div>

      {/* Catégories + Type */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('filters.categories')}</label>
          <CategoryMultiSelect
            expenseCategories={expenseCategories}
            incomeCategories={incomeCategories}
            selection={filters.categories}
            onChange={(categories) => update({ categories })}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('filters.operationType')}</label>
          <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
            {(['all', 'credit', 'debit'] as const).map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => update({ operationType: opt })}
                className={clsx(
                  'flex-1 px-3 py-2 transition-colors',
                  filters.operationType === opt
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50',
                )}
              >
                {t(`filters.opType.${opt}`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Montant */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('filters.amountRange')}</label>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              min="0"
              step="0.01"
              value={filters.amountMin}
              onChange={(e) => update({ amountMin: e.target.value })}
              placeholder={t('filters.amountMin')}
              aria-invalid={!!errors?.amountMin}
              className={clsx(
                'px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2',
                errors?.amountMin
                  ? 'border-rose-400 focus:ring-rose-500 bg-rose-50'
                  : 'border-gray-300 focus:ring-primary-500',
              )}
            />
            <input
              type="number"
              min="0"
              step="0.01"
              value={filters.amountMax}
              onChange={(e) => update({ amountMax: e.target.value })}
              placeholder={t('filters.amountMax')}
              aria-invalid={!!errors?.amountMax}
              className={clsx(
                'px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2',
                errors?.amountMax
                  ? 'border-rose-400 focus:ring-rose-500 bg-rose-50'
                  : 'border-gray-300 focus:ring-primary-500',
              )}
            />
          </div>
          {(errors?.amountMin || errors?.amountMax) && (
            <p className="mt-1 text-xs text-rose-600">{errors.amountMin || errors.amountMax}</p>
          )}
        </div>

        {/* Validation + Partenaire */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('filters.validation')}</label>
            <select
              value={filters.validated}
              onChange={(e) => update({ validated: e.target.value as ValidationMode })}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">{t('filters.validatedOptions.all')}</option>
              <option value="validated">{t('filters.validatedOptions.validated')}</option>
              <option value="pending">{t('filters.validatedOptions.pending')}</option>
            </select>
          </div>

          <div title={partnerFilterDisabled ? t('filters.partnerDisabledHint') : undefined}>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('filters.payer')}</label>
            <select
              value={filters.payerPartnerId}
              onChange={(e) => update({ payerPartnerId: e.target.value as 'all' | 'partner1' | 'partner2' })}
              disabled={partnerFilterDisabled}
              className={clsx(
                'w-full px-3 py-2 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500',
                partnerFilterDisabled
                  ? 'border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50'
                  : 'border-gray-300',
              )}
            >
              <option value="all">{t('filters.payerOptions.all')}</option>
              <option value="partner1">{partner1Name}</option>
              <option value="partner2">{partner2Name}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Récurrence */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{t('filters.recurrence')}</label>
        <div className="space-y-2">
          <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
            {(['all', 'one_time', 'recurring'] as const).map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => update({ recurring: opt, recurrenceFrequencies: opt === 'recurring' ? filters.recurrenceFrequencies : [] })}
                className={clsx(
                  'flex-1 px-3 py-2 transition-colors',
                  filters.recurring === opt
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50',
                )}
              >
                {t(`filters.recurrenceOptions.${opt}`)}
              </button>
            ))}
          </div>

          {filters.recurring === 'recurring' && (
            <div className="flex flex-wrap gap-2">
              {RECURRENCE_FREQUENCY_OPTIONS.map((freq) => {
                const active = filters.recurrenceFrequencies.includes(freq);
                return (
                  <button
                    key={freq}
                    type="button"
                    onClick={() => toggleRecurrenceFrequency(freq)}
                    className={clsx(
                      'px-3 py-1 rounded-full text-xs border transition-colors',
                      active
                        ? 'bg-primary-100 border-primary-300 text-primary-700'
                        : 'bg-white border-gray-300 text-gray-600 hover:border-primary-300',
                    )}
                  >
                    {t(`filters.frequency.${freq}`)}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      {!hideActions && (
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={onReset}
            className="flex items-center justify-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            {t('filters.reset')}
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex items-center justify-center gap-2 px-6 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Search className="w-4 h-4" />
            {isLoading ? tc('loading') : t('filters.submit')}
          </button>
        </div>
      )}
    </form>
  );
}
